import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Switch,
  TextField,
  Box,
  FormControlLabel,
  CircularProgress,
  Button,
  Grid,
  Paper,
} from "@material-ui/core";

import SaveIcon from "@material-ui/icons/Save";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import SettingsIcon from "@material-ui/icons/Settings";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import PersonIcon from "@material-ui/icons/Person";
import WarningIcon from "@material-ui/icons/Warning";

import { Toast } from "components/shared/Toaster";

import {
  getMarketplaceCriteria,
  createMarketplaceCriteria,
  updateMarketplaceCriteria,
} from "actions/user";

/* -------------------------------------------------------------------------- */
/*                                   HELPERS                                  */
/* -------------------------------------------------------------------------- */

const DEFAULT_FMCSA_POLICY = {
  enabled: false,
  minPowerUnits: null,
  maxPowerUnits: null,
  minDrivers: null,
  maxDriverOosRate: null,
  minimumDriverInspections: null,
  maxVehicleOosRate: null,
  minimumVehicleInspections: null,
  maxHazmatOosRate: null,
  minimumHazmatInspections: null,
  requireAllowedToOperate: false,
};

const initialForm = {
  active: false,
  fmcsaPolicy: DEFAULT_FMCSA_POLICY,
};

const normalizeForm = (data = {}) => ({
  active: !!data.active,
  fmcsaPolicy: {
    enabled: !!data.fmcsaPolicy?.enabled,
    minPowerUnits: data.fmcsaPolicy?.minPowerUnits ?? null,
    maxPowerUnits: data.fmcsaPolicy?.maxPowerUnits ?? null,
    minDrivers: data.fmcsaPolicy?.minDrivers ?? null,
    maxDriverOosRate: data.fmcsaPolicy?.maxDriverOosRate ?? null,
    minimumDriverInspections: data.fmcsaPolicy?.minimumDriverInspections ?? null,
    maxVehicleOosRate: data.fmcsaPolicy?.maxVehicleOosRate ?? null,
    minimumVehicleInspections: data.fmcsaPolicy?.minimumVehicleInspections ?? null,
    maxHazmatOosRate: data.fmcsaPolicy?.maxHazmatOosRate ?? null,
    minimumHazmatInspections: data.fmcsaPolicy?.minimumHazmatInspections ?? null,
    requireAllowedToOperate: !!data.fmcsaPolicy?.requireAllowedToOperate,
  },
});

/* -------------------------------------------------------------------------- */
/*                               UI COMPONENTS                                */
/* -------------------------------------------------------------------------- */

const SectionCard = ({ title, icon, children }) => (
  <Paper
    elevation={1}
    style={{
      padding: 16,
      marginBottom: 16,
      borderRadius: 8,
      border: "1px solid #e3f2fd",
      backgroundColor: "var(--bg-canvas)",
    }}
  >
    <Box display="flex" alignItems="center" mb={2}>
      {icon && <Box mr={1} color="primary.main">{icon}</Box>}
      <Typography
        variant="h6"
        style={{ fontWeight: 600, color: "var(--rox-lilac-ink)" }}
      >
        {title}
      </Typography>
    </Box>
    {children}
  </Paper>
);

const NumberInputField = ({ label, value, onChange, placeholder, helperText }) => (
  <TextField
    fullWidth
    label={label}
    variant="outlined"
    size="small"
    type="number"
    placeholder={placeholder}
    value={value || ""}
    onChange={(e) => onChange(e.target.value ? parseInt(e.target.value, 10) : null)}
    InputLabelProps={{ shrink: true }}
    helperText={helperText}
  />
);

/* -------------------------------------------------------------------------- */
/*                               MAIN COMPONENT                               */
/* -------------------------------------------------------------------------- */

const FMCSACriteriaForm = ({ value, onUpdatePolicy, hideSubmitButton = false, onSave }) => {
  const [form, setForm] = useState({ fmcsaPolicy: value || DEFAULT_FMCSA_POLICY });
  const [loading, setLoading] = useState(false);
  const [existingCriteria, setExistingCriteria] = useState(false);

  // Update form when value prop changes
  useEffect(() => {
    if (value) {
      setForm({ fmcsaPolicy: value });
    }
  }, [value]);

  useEffect(() => {
    // Only fetch data if not using controlled mode
    if (!hideSubmitButton) {
      setLoading(true);

      getMarketplaceCriteria().then((res) => {
        const criteria = res?.response?.marketPlacePolicy?.fmcsaPolicy || null;

        if (criteria) {
          setForm(
            normalizeForm({
              ...criteria,
              active: res?.response?.active,
            }),
          );
          setExistingCriteria(true);
        }

        setLoading(false);
      }).finally(() => setLoading(false));
    }
  }, [existingCriteria, hideSubmitButton]);

  const updateForm = useCallback((patch) => {
    const newForm = { ...form, ...patch };
    setForm(newForm);
  }, [form]);

  const updateFmcsaPolicy = useCallback((patch) => {
    const updatedPolicy = { ...form.fmcsaPolicy, ...patch };
    setForm((prev) => ({
      ...prev,
      fmcsaPolicy: updatedPolicy,
    }));

    // If in controlled mode, notify parent of changes
    if (hideSubmitButton && onUpdatePolicy) {
      Object.entries(patch).forEach(([key, value]) => {
        onUpdatePolicy(key, value);
      });
    }
  }, [form.fmcsaPolicy, hideSubmitButton, onUpdatePolicy]);

  const handleSave = () => {
    // Only handle save if not in controlled mode
    if (hideSubmitButton) return;

    const payload = {
      active: form.active,
      marketPlacePolicy: {
        fmcsaPolicy: { ...form.fmcsaPolicy },
      },
    };

    const action = existingCriteria
      ? updateMarketplaceCriteria
      : createMarketplaceCriteria;

    action(payload).then((res) => {
      Toast({
        type: res ? "success" : "error",
        message: res
          ? "FMCSA criteria saved successfully"
          : "Error saving FMCSA criteria",
      });
      if(res){
        setExistingCriteria(true);
      }
    });

    if (onSave) {
      onSave(payload);
    }
  };

  if (loading && !hideSubmitButton) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight={300}
      >
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box>

      {/* FMCSA CONFIGURATION */}
      <SectionCard title="FMCSA Configuration" icon={<SettingsIcon />}>
        <FormControlLabel
          label="Enable FMCSA Checks"
          control={
            <Switch
              color="primary"
              checked={form.fmcsaPolicy.enabled}
              onChange={(e) =>
                updateFmcsaPolicy({
                  enabled: e.target.checked,
                })
              }
            />
          }
        />
        <Typography variant="caption" color="textSecondary" display="block" mb={2}>
          Filter applications based on FMCSA safety records and compliance metrics
        </Typography>

        {form.fmcsaPolicy.enabled && (
          <>
            {/* POWER UNITS & DRIVERS */}
            <SectionCard title="Fleet Size Requirements" icon={<LocalShippingIcon />}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <NumberInputField
                    label="Minimum Power Units"
                    value={form.fmcsaPolicy.minPowerUnits}
                    onChange={(v) => updateFmcsaPolicy({ minPowerUnits: v })}
                    placeholder="e.g. 5"
                    helperText="Minimum number of power units required"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <NumberInputField
                    label="Maximum Power Units"
                    value={form.fmcsaPolicy.maxPowerUnits}
                    onChange={(v) => updateFmcsaPolicy({ maxPowerUnits: v })}
                    placeholder="e.g. 100"
                    helperText="Maximum number of power units allowed"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <NumberInputField
                    label="Minimum Drivers"
                    value={form.fmcsaPolicy.minDrivers}
                    onChange={(v) => updateFmcsaPolicy({ minDrivers: v })}
                    placeholder="e.g. 1"
                    helperText="Minimum number of drivers required"
                  />
                </Grid>
              </Grid>
            </SectionCard>

            {/* DRIVER SAFETY */}
            <SectionCard title="Driver Safety Requirements" icon={<PersonIcon />}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <NumberInputField
                    label="Max Driver OOS Rate (%)"
                    value={form.fmcsaPolicy.maxDriverOosRate}
                    onChange={(v) => updateFmcsaPolicy({ maxDriverOosRate: v })}
                    placeholder="e.g. 15"
                    helperText="Maximum driver out-of-service rate percentage"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <NumberInputField
                    label="Minimum Driver Inspections"
                    value={form.fmcsaPolicy.minimumDriverInspections}
                    onChange={(v) => updateFmcsaPolicy({ minimumDriverInspections: v })}
                    placeholder="e.g. 10"
                    helperText="Minimum number of driver inspections required"
                  />
                </Grid>
              </Grid>
            </SectionCard>

            {/* VEHICLE SAFETY */}
            <SectionCard title="Vehicle Safety Requirements" icon={<LocalShippingIcon />}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <NumberInputField
                    label="Max Vehicle OOS Rate (%)"
                    value={form.fmcsaPolicy.maxVehicleOosRate}
                    onChange={(v) => updateFmcsaPolicy({ maxVehicleOosRate: v })}
                    placeholder="e.g. 20"
                    helperText="Maximum vehicle out-of-service rate percentage"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <NumberInputField
                    label="Minimum Vehicle Inspections"
                    value={form.fmcsaPolicy.minimumVehicleInspections}
                    onChange={(v) => updateFmcsaPolicy({ minimumVehicleInspections: v })}
                    placeholder="e.g. 5"
                    helperText="Minimum number of vehicle inspections required"
                  />
                </Grid>
              </Grid>
            </SectionCard>

            {/* HAZMAT SAFETY */}
            <SectionCard title="Hazmat Safety Requirements" icon={<WarningIcon />}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <NumberInputField
                    label="Max Hazmat OOS Rate (%)"
                    value={form.fmcsaPolicy.maxHazmatOosRate}
                    onChange={(v) => updateFmcsaPolicy({ maxHazmatOosRate: v })}
                    placeholder="e.g. 10"
                    helperText="Maximum hazmat out-of-service rate percentage"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <NumberInputField
                    label="Minimum Hazmat Inspections"
                    value={form.fmcsaPolicy.minimumHazmatInspections}
                    onChange={(v) => updateFmcsaPolicy({ minimumHazmatInspections: v })}
                    placeholder="e.g. 3"
                    helperText="Minimum number of hazmat inspections required"
                  />
                </Grid>
              </Grid>
            </SectionCard>

            {/* OPERATING AUTHORITY */}
            <SectionCard title="Operating Authority" icon={<CheckCircleIcon />}>
              <FormControlLabel
                label="Require Allowed to Operate"
                control={
                  <Switch
                    color="primary"
                    checked={form.fmcsaPolicy.requireAllowedToOperate}
                    onChange={(e) =>
                      updateFmcsaPolicy({
                        requireAllowedToOperate: e.target.checked,
                      })
                    }
                  />
                }
              />
              <Typography variant="caption" color="textSecondary" display="block">
                Require carriers to have valid operating authority
              </Typography>
            </SectionCard>
          </>
        )}
      </SectionCard>

      {/* SAVE BUTTON - Only show if not hiding submit button */}
      {!hideSubmitButton && (
        <Box display="flex" justifyContent="flex-end" mt={3}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save FMCSA Criteria"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default FMCSACriteriaForm;